import { AbstractDisplayObjectClip } from "../clip/AbstractDisplayObjectClip";
import { FlattenedImage } from "./FlattenedImage";
import { DisplayObjectClipSource } from "./concreteSources/DisplayObjectClipSource";
import Bitmap = createjs.Bitmap;
import Rectangle = createjs.Rectangle;
import MovieClip = createjs.MovieClip;
type Info = {
    maxWidth: number,
    maxHeight: number,
    totalWidth: number
};
export class ClipSourceOptimizer {
    private static findFlattenedImageInfo(prev: Info, currentCanvas: FlattenedImage): Info {
        return {
            maxWidth: Math.max(prev.maxWidth, currentCanvas.width),
            maxHeight: Math.max(prev.maxHeight, currentCanvas.height),
            totalWidth: prev.totalWidth + Math.ceil(currentCanvas.width) + 1
        };
    }
    /**
     * Convert an array of `FlattenedImage` to an array of createjs.Bitmap. The aim is to reduce canvas so that we won't have a convas for each
     * flattenedImage, but a huge canvas for all generated `Bitmap`s.
     * @param flattenedImages A array of FlattenedImages to convert.
     */
    public static flattenedImagesToBitmaps(flattenedImages: FlattenedImage[]): Bitmap[] {
        if (flattenedImages.length === 0) { return []; }
        let frameBitmap, resultBitmaps: Bitmap[] = [];
        if (flattenedImages.length === 1) {
            let singleFrame = flattenedImages[0];
            frameBitmap = new Bitmap(singleFrame.canvas);
            frameBitmap.sourceRect = new Rectangle(0, 0, frameBitmap.width, frameBitmap.height);
            frameBitmap.x = -singleFrame.registrationPointX;
            frameBitmap.y = -singleFrame.registrationPointY;
            resultBitmaps.push(frameBitmap);
        } else {
            // combine canvas
            const { maxWidth, maxHeight, totalWidth } = flattenedImages.reduce(
                ClipSourceOptimizer.findFlattenedImageInfo,
                { maxWidth: 0, maxHeight: 0, totalWidth: 0 }
            );
            let clipCanvas = document.createElement("canvas");
            clipCanvas.height = Math.ceil(maxHeight) + 1;
            clipCanvas.width = totalWidth;
            let clipCtx = clipCanvas.getContext("2d");
            let startX = 0;
            flattenedImages.forEach((flattenedImage) => {
                clipCtx.drawImage(flattenedImage.canvas, 0, 0, flattenedImage.width, flattenedImage.height, startX, 0, flattenedImage.width, flattenedImage.height);
                frameBitmap = new Bitmap(clipCanvas);
                frameBitmap.sourceRect = new Rectangle(startX, 0, flattenedImage.width, flattenedImage.height);
                frameBitmap.x = -flattenedImage.registrationPointX;
                frameBitmap.y = -flattenedImage.registrationPointY;
                resultBitmaps.push(frameBitmap);
                startX += Math.ceil(flattenedImage.width) + 1;
                flattenedImage.dispose();
            });
        }
        return resultBitmaps;
    }

    public static maxSublayerMCFramesReducer(prev: number, child: MovieClip): number {
        return child instanceof MovieClip ? (prev >= child.totalFrames ? prev : child.totalFrames) : prev;
    }

    /**
     * Convert a createjs.Movieclip to an array of bitmaps.
     * @param {MovieClip} source movieclip that will be played
     * @param {DisplayObjectClipSource | AbstractDisplayObjectClip} clip  clipsource
     * @param {Function} singleFrameRender A function that renders a single frame to bitmap
     */
    public static ClipMCToBitmaps(source: MovieClip, clip: DisplayObjectClipSource | AbstractDisplayObjectClip, singleFrameRender: Function): Bitmap[] {
        let frameCanvas: FlattenedImage[] = [], resultBitmaps: Bitmap[];
        if (clip.hasSubLayer) {
            let maxFrames: number = source.subLayerMaxFrame || 0;
            let subMC: MovieClip;

            if (!maxFrames) {
                source.subLayerMaxFrame = maxFrames = source.children.reduce(ClipSourceOptimizer.maxSublayerMCFramesReducer, maxFrames);
            }

            for (let k: number = 1; k < maxFrames + 1; k++) {
                for (let l: number = 0; l < source.children.length; l++) {
                    subMC = source.children[l] as MovieClip;
                    if (subMC instanceof MovieClip) {
                        subMC.gotoAndStop(k);
                    }
                }
                frameCanvas.push(singleFrameRender.call(clip, source));
            }
        } else {
            resultBitmaps = [];
            if (source.totalFrames === 1 && source.children.length === 1) {
                let onlyChild = source.children[0];
                if (onlyChild instanceof Bitmap) {
                    resultBitmaps.push(onlyChild.clone());
                    return resultBitmaps;
                } else if (onlyChild instanceof MovieClip && onlyChild.totalFrames === 1 && onlyChild.children.length === 1 && onlyChild.children[0] instanceof Bitmap
                    && !onlyChild.filters && !onlyChild.mask && onlyChild.scaleX === 1 && onlyChild.scaleY === 1 && !onlyChild.skewX && !onlyChild.skewY && !onlyChild.rotation) {
                    let onlyGrandChild = onlyChild.children[0].clone() as Bitmap;
                    onlyGrandChild.x += onlyChild.x;
                    onlyGrandChild.y += onlyChild.y;
                    resultBitmaps.push(onlyGrandChild);
                    return resultBitmaps;
                }
            }
            let isMovieClipWithSimpleBitmap = true;
            for (let i: number = 1; i < source.totalFrames + 1; i++) {
                source.gotoAndStop(i);
                if (isMovieClipWithSimpleBitmap && source.children.length === 1 && source.children[0] instanceof Bitmap) {
                    resultBitmaps.push(source.children[0].clone() as Bitmap);
                } else {
                    isMovieClipWithSimpleBitmap = false;
                }
                frameCanvas.push(singleFrameRender.call(clip, source));
            }
            if (isMovieClipWithSimpleBitmap) {
                frameCanvas.forEach( flattenedimg => flattenedimg.dispose());
                source.uncache();
                return resultBitmaps;
            }
        }
        source.uncache();
        return resultBitmaps = ClipSourceOptimizer.flattenedImagesToBitmaps(frameCanvas);
    }

}
